import React, { createContext, useContext, useState, useEffect } from 'react';
import { businessAPI } from '../services/api';

const BusinessContext = createContext(null);

export const useBusiness = () => {
  const context = useContext(BusinessContext);
  if (!context) {
    throw new Error('useBusiness must be used within a BusinessProvider');
  }
  return context;
};

export const BusinessProvider = ({ children }) => {
  const [activeBusiness, setActiveBusiness] = useState(null);
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Load active business from localStorage
    const storedBusiness = localStorage.getItem('active_business');
    if (storedBusiness) {
      try {
        const business = JSON.parse(storedBusiness);
        console.log('[BusinessContext] Loading business from localStorage:', business);
        setActiveBusiness(business);
        
        // Also load features if we have business ID
        if (business.id) {
          loadFeatures(business.id);
        }
      } catch (error) {
        console.error('[BusinessContext] Failed to parse stored business:', error);
        // Fallback to API load if parse fails
        const storedBusinessId = localStorage.getItem('active_business_id');
        if (storedBusinessId) {
          loadBusiness(storedBusinessId);
        }
      }
    } else {
      // No stored business - try to load by ID
      const storedBusinessId = localStorage.getItem('active_business_id');
      if (storedBusinessId) {
        loadBusiness(storedBusinessId);
      }
    }
  }, []);

  const loadFeatures = async (businessId) => {
    try {
      const featuresRes = await businessAPI.getFeatures(businessId);
      setFeatures(featuresRes.data.features || []);
    } catch (error) {
      console.error('[BusinessContext] Failed to load features:', error);
      // Fail silently - features are optional
      setFeatures([]);
    }
  };

  const loadBusiness = async (businessId) => {
    try {
      setLoading(true);
      
      // Load business first
      const businessRes = await businessAPI.get(businessId);
      setActiveBusiness(businessRes.data);
      localStorage.setItem('active_business_id', businessId);
      localStorage.setItem('active_business', JSON.stringify(businessRes.data));
      
      // Load features separately (optional)
      try {
        const featuresRes = await businessAPI.getFeatures(businessId);
        setFeatures(featuresRes.data.features || []);
      } catch (featError) {
        console.warn('[BusinessContext] Features API not available:', featError.message);
        setFeatures([]);
      }
    } catch (error) {
      console.error('[BusinessContext] Failed to load business:', error);
      // If business API fails, try to use cached data
      const storedBusiness = localStorage.getItem('active_business');
      if (storedBusiness) {
        try {
          const business = JSON.parse(storedBusiness);
          console.log('[BusinessContext] Using cached business data');
          setActiveBusiness(business);
        } catch (e) {
          console.error('[BusinessContext] Failed to parse cached business:', e);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const switchBusiness = async (businessId) => {
    try {
      // First check if we have this business in businesses from auth
      const businessesStr = localStorage.getItem('user_businesses');
      if (businessesStr) {
        try {
          const businesses = JSON.parse(businessesStr);
          const businessWithRole = businesses.find(b => b.id === businessId);
          if (businessWithRole) {
            setActiveBusiness(businessWithRole);
            localStorage.setItem('active_business_id', businessId);
            localStorage.setItem('active_business', JSON.stringify(businessWithRole));
            
            // Load features (optional)
            try {
              const featuresRes = await businessAPI.getFeatures(businessId);
              setFeatures(featuresRes.data.features || []);
            } catch (error) {
              console.warn('Features API not available:', error.message);
              setFeatures([]);
            }
            return;
          }
        } catch (e) {
          console.error('Failed to parse stored businesses:', e);
        }
      }
      
      // Fallback to API - this will throw if access denied
      await loadBusiness(businessId);
    } catch (error) {
      console.error('[BusinessContext] switchBusiness failed:', error);
      throw error; // Re-throw to allow caller to handle
    }
  };

  const hasFeature = (featureCode) => {
    return features.includes(featureCode);
  };

  const value = {
    activeBusiness,
    features,
    loading,
    switchBusiness,
    hasFeature,
  };

  return (
    <BusinessContext.Provider value={value}>{children}</BusinessContext.Provider>
  );
};